import React, { useCallback, useEffect, useState } from "react";
import { Button } from "flowbite-react";
import { toast } from "react-toastify";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAppSelector } from "../../Reducers/store";
import { UserLevelNames } from "../../Models/UserLevel";
import QBApi, { QBConnectionStatus, QBUnsentItem } from "../../Api/QBApi";
import Loader from "../Common/Loader";

const AdminQuickBooksPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userModel } = useAppSelector((state) => state.user);
  const [status, setStatus] = useState<QBConnectionStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [unsent, setUnsent] = useState<QBUnsentItem[]>([]);
  const [unsentLoading, setUnsentLoading] = useState(false);
  const [resubmittingKey, setResubmittingKey] = useState<string | null>(null);
  const [resubmittingAll, setResubmittingAll] = useState(false);

  useEffect(() => {
    if (userModel && userModel.userLevelId !== UserLevelNames.SuperAdmin) {
      toast.error("You do not have permission to access this page.");
      navigate("/");
    }
  }, [userModel, navigate]);

  const loadUnsent = useCallback(async () => {
    try {
      setUnsentLoading(true);
      const res = await QBApi.getUnsent();
      setUnsent(res.data ?? []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load items that were not sent to QuickBooks.");
      setUnsent([]);
    } finally {
      setUnsentLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await QBApi.getStatus();
      setStatus(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load QuickBooks status.");
      setStatus(null);
    } finally {
      setLoading(false);
    }
    await loadUnsent();
  }, [loadUnsent]);

  useEffect(() => {
    if (userModel?.userLevelId === UserLevelNames.SuperAdmin) {
      load();
    }
  }, [userModel, load]);

  useEffect(() => {
    const qb = searchParams.get("qb");
    if (!qb) return;
    if (qb === "connected") {
      toast.success("QuickBooks reconnected successfully.");
      load();
    } else if (qb === "error") {
      toast.error(
        searchParams.get("message") || "QuickBooks reconnect failed."
      );
    }
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, load]);

  const reconnect = async () => {
    try {
      setReconnecting(true);
      const res = await QBApi.getReconnectUrl();
      if (!res.data?.authorizationUrl) {
        toast.error("Could not build QuickBooks authorization URL.");
        return;
      }
      window.location.href = res.data.authorizationUrl;
    } catch (err) {
      console.error(err);
      toast.error("Failed to start QuickBooks reconnect.");
      setReconnecting(false);
    }
  };

  const connected = !!status?.connected;

  const formatDate = (value?: string | null) => {
    if (!value) return "—";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleDateString();
  };

  const formatAmount = (value?: number | null) => {
    if (value == null) return "—";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  const itemKey = (item: QBUnsentItem) => `${item.kind}-${item.id}`;

  const resubmitOne = async (item: QBUnsentItem) => {
    const key = itemKey(item);
    try {
      setResubmittingKey(key);
      const res = await QBApi.resubmit(item.kind, item.id);
      if (res.data?.success) {
        toast.success(res.data.message || "Sent to QuickBooks.");
        await loadUnsent();
      } else {
        toast.error(res.data?.message || "QuickBooks resubmit failed.");
        await loadUnsent();
      }
    } catch (err) {
      console.error(err);
      toast.error("QuickBooks resubmit failed.");
    } finally {
      setResubmittingKey(null);
    }
  };

  const resubmitAll = async () => {
    if (unsent.length === 0) return;
    try {
      setResubmittingAll(true);
      const res = await QBApi.resubmitAll();
      const results = res.data ?? [];
      const succeeded = results.filter((r) => r.success).length;
      const failed = results.length - succeeded;
      if (failed === 0) {
        toast.success(`Sent ${succeeded} item${succeeded === 1 ? "" : "s"} to QuickBooks.`);
      } else {
        toast.error(
          `Sent ${succeeded}, failed ${failed}. Check the remaining rows for errors.`
        );
      }
      await loadUnsent();
    } catch (err) {
      console.error(err);
      toast.error("Bulk resubmit failed.");
    } finally {
      setResubmittingAll(false);
    }
  };

  return (
    <div className="w-full max-w-6xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">QuickBooks</h1>
      <p className="text-sm text-gray-600 mb-6">
        Monitor the QuickBooks Online connection used for invoices and customer
        sync. Super users are emailed when this connection goes down.
      </p>

      {loading && <Loader />}

      {!loading && status && (
        <div
          className={`rounded-lg border p-4 mb-6 ${
            connected
              ? "border-green-300 bg-green-50"
              : "border-rose-300 bg-rose-50"
          }`}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <div
                className={`text-lg font-semibold ${
                  connected ? "text-green-800" : "text-rose-800"
                }`}
              >
                {connected ? "Connected" : "Disconnected"}
              </div>
              <p className="text-sm text-gray-700 mt-1">{status.message}</p>
            </div>
            <div className="flex gap-2">
              <Button color="light" onClick={() => void load()} disabled={loading}>
                Refresh status
              </Button>
              <Button
                className="buttonMain"
                onClick={() => void reconnect()}
                disabled={reconnecting}
              >
                {reconnecting ? "Redirecting…" : "Reconnect QuickBooks"}
              </Button>
            </div>
          </div>

          <ul className="mt-4 text-sm text-gray-700 space-y-1">
            <li>
              Access token:{" "}
              {status.hasAccessToken ? "present" : "missing / invalid"}
            </li>
            <li>
              Refresh token:{" "}
              {status.hasRefreshToken ? "present" : "missing / invalid"}
            </li>
            <li>Company (realm): {status.hasRealmId ? "set" : "missing"}</li>
          </ul>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white p-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Not sent to QuickBooks
            </h2>
            <p className="text-sm text-gray-600">
              Paid form invoices and approved members that should have a
              QuickBooks record but do not.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              color="light"
              onClick={() => void loadUnsent()}
              disabled={unsentLoading || resubmittingAll}
            >
              Refresh list
            </Button>
            <Button
              className="buttonMain"
              onClick={() => void resubmitAll()}
              disabled={
                !connected ||
                unsent.length === 0 ||
                resubmittingAll ||
                resubmittingKey !== null
              }
            >
              {resubmittingAll ? "Resubmitting…" : "Resubmit all"}
            </Button>
          </div>
        </div>

        {unsentLoading && <Loader />}

        {!unsentLoading && unsent.length === 0 && (
          <p className="text-sm text-gray-600">
            Nothing is waiting to be sent.
          </p>
        )}

        {!unsentLoading && unsent.length > 0 && (
          <div className="overflow-auto border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Item</th>
                  <th className="text-left px-3 py-2">Company</th>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Amount</th>
                  <th className="text-left px-3 py-2">Why</th>
                  <th className="text-left px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {unsent.map((item) => {
                  const key = itemKey(item);
                  const busy = resubmittingKey === key || resubmittingAll;
                  return (
                    <tr key={key} className="border-t align-top">
                      <td className="px-3 py-2 whitespace-nowrap">
                        {item.kind === "MemberCustomer"
                          ? "Customer"
                          : "Invoice"}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">
                          {item.title}
                        </div>
                        {item.submittedBy ? (
                          <div className="text-gray-500">{item.submittedBy}</div>
                        ) : null}
                        {item.lastError ? (
                          <div className="text-rose-700 text-xs mt-1 break-all">
                            {item.lastError}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">{item.companyName || "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {formatDate(item.date)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {formatAmount(item.amount)}
                      </td>
                      <td className="px-3 py-2 text-gray-700">{item.reason}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex flex-col items-start gap-1">
                          <Button
                            size="xs"
                            className="buttonMain"
                            onClick={() => void resubmitOne(item)}
                            disabled={!connected || busy}
                          >
                            {resubmittingKey === key
                              ? "Sending…"
                              : "Resubmit"}
                          </Button>
                          {item.kind === "FormInvoice" && item.formId ? (
                            <Link
                              className="text-blue-700 underline text-xs"
                              to={`/forms/${item.formId}/responses`}
                            >
                              View form
                            </Link>
                          ) : null}
                          {item.kind === "MemberCustomer" && item.memberId ? (
                            <Link
                              className="text-blue-700 underline text-xs"
                              to={`/edit/member/${item.memberId}`}
                            >
                              View member
                            </Link>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 space-y-2">
        <div className="font-semibold text-gray-900">Reconnect setup</div>
        <p>
          The Intuit Developer app must include this exact Redirect URI:
        </p>
        <code className="block break-all bg-white border border-gray-200 rounded px-2 py-1 text-xs">
          {status?.redirectUri ||
            "https://emdaapi.azurewebsites.net/api/qb/oauth/callback"}
        </code>
        <p>
          After authorizing, Intuit returns here and EMDA saves new tokens, then
          sends you back to this page.
        </p>
        <p>
          Need help? Return to the{" "}
          <Link className="text-blue-700 underline" to="/">
            dashboard
          </Link>
          .
        </p>
      </div>
    </div>
  );
};

export default AdminQuickBooksPage;
